import React, { useState, useEffect } from "react";
import {
	Card,
	Table,
	Input,
	Select,
	Button,
	message,
	Typography,
	Tag,
	Row,
	Col,
	DatePicker,
} from "antd";
import {
	SwapOutlined,
	FilterOutlined,
	PlusOutlined,
	DownloadOutlined,
	EditOutlined,
} from "@ant-design/icons";
import useAxios from "../../utilities/useAxios";
import dayjs from "dayjs";
import AddTransferRecordModal from "./AddTransferRecordModal";
import EditTransferRecordModal from "./EditTransferRecordModal";
import useIsMobile from "../../hooks/useIsMobile";
import PageHeader from "../common/PageHeader";

const { Text } = Typography;
const { Option } = Select;

const ViewTransferRecords = () => {
	const isMobile = useIsMobile();
	const [records, setRecords] = useState([]);
	const [loading, setLoading] = useState(true);
	const [searchText, setSearchText] = useState("");
	const [typeFilter, setTypeFilter] = useState("all");
	const [dateRangeFilter, setDateRangeFilter] = useState(null);
	const [addModalVisible, setAddModalVisible] = useState(false);
	const [editModalVisible, setEditModalVisible] = useState(false);
	const [selectedTransferRecord, setSelectedTransferRecord] =
		useState(null);

	useEffect(() => {
		fetchAllTransferRecords();
	}, []);

	const fetchAllTransferRecords = async () => {
		try {
			setLoading(true);
			const response = await useAxios.get(
				"/users/all-transfer-records/"
			);
			if (response.status === 200) {
				setRecords(response.data);
			}
		} catch (error) {
			message.error("이명 기록을 불러오는데 실패했습니다.");
		} finally {
			setLoading(false);
		}
	};

	const handleSearch = (value) => {
		setSearchText(value);
	};

	const handleTypeFilterChange = (value) => {
		setTypeFilter(value);
	};

	const handleDateRangeFilterChange = (dates) => {
		setDateRangeFilter(dates);
	};

	const handleAddSuccess = () => {
		setAddModalVisible(false);
		fetchAllTransferRecords(); // Refresh the list
		message.success("이명 기록이 성공적으로 추가되었습니다.");
	};

	const handleEdit = (record) => {
		setSelectedTransferRecord(record);
		setEditModalVisible(true);
	};

	const handleEditSuccess = () => {
		setEditModalVisible(false);
		setSelectedTransferRecord(null);
		fetchAllTransferRecords(); // Refresh the list
	};

	const handleEditCancel = () => {
		setEditModalVisible(false);
		setSelectedTransferRecord(null);
	};

	const getTransferColor = (type) => {
		switch (type) {
			case "전입":
				return "green";
			case "전출":
				return "red";
			default:
				return "default";
		}
	};

	const getClassificationColor = (classification) => {
		switch (classification) {
			case "성도":
				return "blue";
			case "교직원":
				return "green";
			case "교역자":
				return "purple";
			case "은퇴목사":
				return "orange";
			default:
				return "default";
		}
	};

	const filteredRecords = records.filter((record) => {
		// Search filter
		if (searchText) {
			const searchValue =
				record.user_name?.toLowerCase() || "";
			if (!searchValue.includes(searchText.toLowerCase())) {
				return false;
			}
		}

		// Type filter
		if (
			typeFilter !== "all" &&
			record.transfer_type !== typeFilter
		) {
			return false;
		}

		// Date range filter
		if (
			dateRangeFilter &&
			dateRangeFilter[0] &&
			dateRangeFilter[1]
		) {
			const recordDate = dayjs(record.transfer_date);
			const startDate = dateRangeFilter[0];
			const endDate = dateRangeFilter[1];
			if (
				!recordDate.isBetween(
					startDate,
					endDate,
					"day",
					"[]"
				)
			) {
				return false;
			}
		}

		return true;
	});

	const handleDownloadExcel = () => {
		try {
			// Prepare data for Excel
			const excelData = filteredRecords.map((record) => ({
				성도명: record.user_name || "-",
				생년월일: record.birthdate
					? dayjs(record.birthdate).format(
							"YYYY-MM-DD"
					  )
					: "-",
				분류: record.classification_name || "-",
				소속: record.affiliated_group || "-",
				이명유형: record.transfer_type || "-",
				이명일: record.transfer_date
					? dayjs(record.transfer_date).format(
							"YYYY-MM-DD"
					  )
					: "-",
				해당교회: record.corressponding_church || "-",
				이명사유: record.reason || "-",
			}));

			// Convert to CSV format
			const headers = Object.keys(excelData[0] || {});
			const csvContent = [
				headers.join(","),
				...excelData.map((row) =>
					headers
						.map((header) => {
							const value =
								row[header] ||
								"";
							// Escape commas and quotes in the value
							if (
								value.includes(
									","
								) ||
								value.includes(
									'"'
								) ||
								value.includes(
									"\n"
								)
							) {
								return `"${value.replace(
									/"/g,
									'""'
								)}"`;
							}
							return value;
						})
						.join(",")
				),
			].join("\n");

			// Create and download file
			const blob = new Blob(["\uFEFF" + csvContent], {
				type: "text/csv;charset=utf-8;",
			});
			const link = document.createElement("a");
			const url = URL.createObjectURL(blob);
			link.setAttribute("href", url);
			link.setAttribute(
				"download",
				`이명기록_${dayjs().format(
					"YYYY-MM-DD_HH-mm"
				)}.csv`
			);
			link.style.visibility = "hidden";
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);

			message.success(
				"엑셀 파일이 성공적으로 다운로드되었습니다."
			);
		} catch (error) {
			message.error("엑셀 파일 다운로드에 실패했습니다.");
		}
	};

	const columns = [
		{
			title: "성도명",
			dataIndex: "user_name",
			key: "user_name",
			sorter: (a, b) =>
				(a.user_name || "").localeCompare(
					b.user_name || ""
				),
			render: (name) => <Text strong>{name || "-"}</Text>,
		},
		{
			title: "생년월일",
			dataIndex: "birthdate",
			key: "birthdate",
			sorter: (a, b) =>
				dayjs(a.birthdate || 0).unix() -
				dayjs(b.birthdate || 0).unix(),
			render: (date) =>
				date ? dayjs(date).format("YYYY-MM-DD") : "-",
		},
		{
			title: "분류",
			dataIndex: "classification_name",
			key: "classification_name",
			sorter: (a, b) =>
				(a.classification_name || "").localeCompare(
					b.classification_name || ""
				),
			render: (classification) => (
				<Tag
					color={getClassificationColor(
						classification
					)}>
					{classification || "-"}
				</Tag>
			),
		},
		{
			title: "소속",
			dataIndex: "affiliated_group",
			key: "affiliated_group",
			sorter: (a, b) =>
				(a.affiliated_group || "").localeCompare(
					b.affiliated_group || ""
				),
			render: (group) => <Text>{group || "-"}</Text>,
		},
		{
			title: "이명 유형",
			dataIndex: "transfer_type",
			key: "transfer_type",
			sorter: (a, b) =>
				(a.transfer_type || "").localeCompare(
					b.transfer_type || ""
				),
			render: (type) => (
				<Tag color={getTransferColor(type)}>{type}</Tag>
			),
		},
		{
			title: "이명일",
			dataIndex: "transfer_date",
			key: "transfer_date",
			sorter: (a, b) =>
				dayjs(a.transfer_date).unix() -
				dayjs(b.transfer_date).unix(),
			render: (date) => dayjs(date).format("YYYY-MM-DD"),
			defaultSortOrder: "descend",
		},
		{
			title: "해당교회",
			dataIndex: "corressponding_church",
			key: "corressponding_church",
			sorter: (a, b) =>
				(a.corressponding_church || "").localeCompare(
					b.corressponding_church || ""
				),
			render: (church) => <Text>{church || "-"}</Text>,
		},
		{
			title: "이명사유",
			dataIndex: "reason",
			key: "reason",
			render: (reason) => (
				<Text
					style={{
						maxWidth: 200,
						display: "block",
					}}>
					{reason || "-"}
				</Text>
			),
		},
		{
			title: "작업",
			key: "actions",
			render: (_, record) => (
				<Button
					type="primary"
					size="small"
					icon={<EditOutlined />}
					onClick={() => handleEdit(record)}>
					수정
				</Button>
			),
		},
	];

	return (
		<div className="page" style={{ padding: "24px" }}>
			<PageHeader
				title={
					<>
						<SwapOutlined /> 이명 기록 관리
					</>
				}
				subtitle="모든 성도의 이명 기록을 조회하고 관리할 수 있습니다."
			/>
			<Card className="surface-card">

				{/* Search and Filter Section */}
				<div
					style={{
						marginTop: 24,
						marginBottom: 16,
					}}>
					<Row gutter={[16, 16]} align="middle">
						<Col xs={24} sm={12} md={5}>
							<Input.Search
								placeholder="성도명으로 검색"
								allowClear
								onSearch={
									handleSearch
								}
								style={{
									width: "100%",
								}}
							/>
						</Col>
						<Col xs={24} sm={12} md={3}>
							<Select
								placeholder="이명 유형"
								value={
									typeFilter
								}
								onChange={
									handleTypeFilterChange
								}
								style={{
									width: "100%",
								}}
								allowClear>
								<Option value="all">
									전체
									유형
								</Option>
								<Option value="전입">
									<Tag color="green">
										전입
									</Tag>
								</Option>
								<Option value="전출">
									<Tag color="red">
										전출
									</Tag>
								</Option>
							</Select>
						</Col>
						<Col xs={24} sm={12} md={5}>
							<DatePicker.RangePicker
								inputReadOnly={isMobile}
								placeholder={[
									"시작일",
									"종료일",
								]}
								value={
									dateRangeFilter
								}
								onChange={
									handleDateRangeFilterChange
								}
								style={{
									width: "100%",
								}}
								format="YYYY-MM-DD"
							/>
						</Col>
						<Col xs={24} sm={12} md={3}>
							<Button
								type="primary"
								icon={
									<PlusOutlined />
								}
								onClick={() =>
									setAddModalVisible(
										true
									)
								}
								style={{
									width: "100%",
								}}>
								이명 기록 추가
							</Button>
						</Col>
						<Col xs={24} sm={12} md={3}>
							<Button
								icon={
									<DownloadOutlined />
								}
								onClick={
									handleDownloadExcel
								}
								style={{
									width: "100%",
								}}>
								엑셀 다운로드
							</Button>
						</Col>
						<Col xs={24} sm={12} md={2}>
							<Button
								icon={
									<FilterOutlined />
								}
								onClick={() => {
									setSearchText(
										""
									);
									setTypeFilter(
										"all"
									);
									setDateRangeFilter(
										null
									);
								}}
								style={{
									width: "100%",
								}}>
								필터 초기화
							</Button>
						</Col>
					</Row>
				</div>

				{/* Records Table */}
				<Table
					columns={columns}
					dataSource={filteredRecords}
					rowKey="id"
					loading={loading}
					pagination={{
						pageSize: 20,
						showSizeChanger: true,
						showQuickJumper: true,
						showTotal: (total, range) =>
							`${range[0]}-${range[1]} / 총 ${total}개`,
					}}
					locale={{
						emptyText: "이명 기록이 없습니다.",
					}}
					scroll={{ x: 1400 }}
				/>
			</Card>

			{/* Add Transfer Record Modal */}
			<AddTransferRecordModal
				visible={addModalVisible}
				onCancel={() => setAddModalVisible(false)}
				onSuccess={handleAddSuccess}
			/>

			{/* Edit Transfer Record Modal */}
			<EditTransferRecordModal
				visible={editModalVisible}
				onCancel={handleEditCancel}
				onSuccess={handleEditSuccess}
				transferRecord={selectedTransferRecord}
			/>
		</div>
	);
};

export default ViewTransferRecords;
